import React, { useMemo } from 'react';

const CustomMidiRoll = ({ notes, activeNotes, isRecording, analysisResults }) => {
  // Force re-render during recording for smooth scrolling
  const [, forceUpdate] = React.useReducer(x => x + 1, 0);
  
  // Scroll position state (0 = start, 1 = end)
  const [scrollPosition, setScrollPosition] = React.useState(1);
  const scrollContainerRef = React.useRef(null);
  
  React.useEffect(() => {
    if (isRecording) {
      // Update every 50ms for smooth scrolling during recording
      const interval = setInterval(() => {
        forceUpdate();
      }, 50);
      // Always scroll to the end during recording
      setScrollPosition(1);
      return () => clearInterval(interval);
    }
  }, [isRecording]);
  
  // Reset scroll to end when recording stops
  React.useEffect(() => {
    if (!isRecording && notes.length > 0) {
      setScrollPosition(1); // Show the most recent notes
    }
  }, [isRecording, notes.length]);
  
  
  // Piano range (21 = A0, 108 = C8)
  const minNote = 21;
  const maxNote = 108;
  const noteRange = maxNote - minNote + 1;
  
  // Get note name from MIDI number
  const getNoteNameFromMIDI = (noteNumber) => {
    const noteNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    const octave = Math.floor(noteNumber / 12) - 1;
    const noteName = noteNames[noteNumber % 12];
    return `${noteName}${octave}`;
  };

  // Check if note is a black key
  const isBlackKey = (noteNumber) => {
    return [1, 3, 6, 8, 10].includes(noteNumber % 12);
  };

  // Get color based on hand classification
  const getNoteColor = (note) => {
    if (note.hand === 'left') return 'bg-blue-500';
    if (note.hand === 'right') return 'bg-green-500';
    return 'bg-orange-400'; // Unclassified
  };

  // Process notes for display
  const processedNotes = useMemo(() => {
    const allNotes = [...notes];
    
    // Add active notes (currently being held)
    activeNotes.forEach(activeNote => {
      allNotes.push({
        ...activeNote,
        isActive: true
      });
    });

    // Sort by timestamp
    return allNotes.sort((a, b) => a.timestamp - b.timestamp);
  }, [notes, activeNotes]);

  // Calculate time window and total session duration
  const timeWindowMs = 10000; // 10 seconds visible at once
  const currentTime = Date.now();
  
  // Calculate total session duration
  let sessionStartTime = currentTime - timeWindowMs;
  let sessionEndTime = currentTime;
  
  if (notes.length > 0) {
    // Find the earliest and latest note times
    const earliestNote = Math.min(...notes.map(note => note.startTime));
    const latestNoteEnd = Math.max(...notes.map(note => note.startTime + (note.duration || 0)));
    sessionStartTime = earliestNote;
    sessionEndTime = latestNoteEnd;
  }
  
  const totalSessionDuration = sessionEndTime - sessionStartTime;
  const needsScrollbar = !isRecording && totalSessionDuration > timeWindowMs;
  
  // Keyboard navigation for scrollbar
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isRecording && needsScrollbar) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          setScrollPosition(prev => Math.max(0, prev - 0.1));
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          setScrollPosition(prev => Math.min(1, prev + 0.1));
        } else if (e.key === 'Home') {
          e.preventDefault();
          setScrollPosition(0);
        } else if (e.key === 'End') {
          e.preventDefault();
          setScrollPosition(1);
        }
      }
    };
    
    // Only add listener if scrollbar is needed
    if (needsScrollbar) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isRecording, needsScrollbar]);
  
  // Calculate display window based on recording state and scroll position
  let displayEndTime, displayStartTime;
  
  if (isRecording) {
    // Live scrolling during recording - notes appear from the right and scroll left
    displayEndTime = currentTime + 500; // Add small buffer to see notes appear
    displayStartTime = currentTime - timeWindowMs + 500;
  } else {
    // After recording: use scroll position to determine view
    if (needsScrollbar) {
      // Calculate position based on scroll (0 = start, 1 = end)
      const scrollableRange = totalSessionDuration - timeWindowMs;
      const scrollOffset = scrollPosition * scrollableRange;
      displayStartTime = sessionStartTime + scrollOffset;
      displayEndTime = displayStartTime + timeWindowMs;
    } else {
      // Session fits in window, show all
      displayStartTime = sessionStartTime - 500;
      displayEndTime = sessionEndTime + 500;
    }
  }

  // Filter notes to show only those in the display window
  const visibleNotes = processedNotes.filter(note => {
    const noteEndTime = note.startTime + (note.duration || 0);
    return noteEndTime > displayStartTime && note.startTime < displayEndTime;
  });

  
  // Hand distribution calculation (removed logging)
  if (visibleNotes.length > 0) {
    const handCounts = {
      left: visibleNotes.filter(n => n.hand === 'left').length,
      right: visibleNotes.filter(n => n.hand === 'right').length,
      unclassified: visibleNotes.filter(n => !n.hand || (n.hand !== 'left' && n.hand !== 'right')).length
    };
  }

  return (
    <div className="w-full h-full bg-gray-900 relative overflow-hidden">
      {/* Piano keys on the left */}
      <div className="absolute left-0 top-0 w-16 h-full bg-gray-800 border-r border-gray-600">
        {Array.from({ length: noteRange }, (_, i) => {
          const noteNumber = minNote + i;
          const yPos = ((noteRange - i - 1) / noteRange) * 100;
          const keyHeight = 100 / noteRange;
          
          return (
            <div
              key={noteNumber}
              className={`absolute w-full text-xs flex items-center justify-center text-white font-mono border-b border-gray-700 ${
                isBlackKey(noteNumber) ? 'bg-gray-700' : 'bg-gray-600'
              }`}
              style={{
                top: `${yPos}%`,
                height: `${keyHeight}%`,
                fontSize: '8px'
              }}
            >
              {keyHeight > 1.5 ? getNoteNameFromMIDI(noteNumber) : ''}
            </div>
          );
        })}
      </div>

      {/* Note display area */}
      <div className="absolute left-16 top-0 right-0 h-full bg-gray-900">
        {/* Time grid lines */}
        <div className="absolute inset-0">
          {Array.from({ length: 11 }, (_, i) => (
            <div
              key={i}
              className="absolute top-0 bottom-0 w-px bg-gray-700"
              style={{ left: `${(i / 10) * 100}%` }}
            />
          ))}
        </div>

        {/* Horizontal grid lines for notes */}
        <div className="absolute inset-0">
          {Array.from({ length: noteRange + 1 }, (_, i) => (
            <div
              key={i}
              className="absolute left-0 right-0 h-px bg-gray-800"
              style={{ top: `${(i / noteRange) * 100}%` }}
            />
          ))}
        </div>

        {/* Notes */}
        {visibleNotes.map((note, index) => {
          const noteNumber = note.note;
          const noteIndex = noteNumber - minNote;
          
          // Calculate position
          const yPos = ((noteRange - noteIndex - 1) / noteRange) * 100;
          const noteHeight = 100 / noteRange;
          
          // Calculate time position (right edge is display end time)
          const noteStartTime = note.startTime || (currentTime - (note.timestamp * 1000));
          
          // For active notes, extend to current time; for completed notes, use the recorded duration
          let noteEndTime;
          if (note.isActive) {
            // Active note: extend to current time
            noteEndTime = currentTime;
          } else {
            // Completed note: use recorded duration
            const noteDuration = note.duration || 100; // Default 100ms if no duration
            noteEndTime = noteStartTime + noteDuration;
          }
          
          // Position in time window (0 = left edge, 1 = right edge)
          const rightPos = 1 - ((displayEndTime - noteEndTime) / timeWindowMs);
          const leftPos = 1 - ((displayEndTime - noteStartTime) / timeWindowMs);
          const width = Math.max(0.5, (rightPos - leftPos) * 100); // Min 0.5% width
          
          // Skip notes that are completely off-screen
          if (rightPos < 0 || leftPos > 1) {
            return null;
          }
          
          return (
            <div
              key={`${note.timestamp}-${note.note}-${index}`}
              className={`absolute rounded-sm ${getNoteColor(note)} ${note.isActive ? 'animate-pulse opacity-90 shadow-lg' : 'opacity-80'} transition-all duration-100`}
              style={{
                top: `${yPos}%`,
                height: `${Math.max(0.8, noteHeight)}%`,
                left: `${Math.max(0, leftPos * 100)}%`,
                width: `${width}%`,
                minWidth: '3px',
                boxShadow: note.isActive ? '0 0 10px rgba(255,255,255,0.5)' : 'none'
              }}
              title={`${getNoteNameFromMIDI(noteNumber)} - ${note.hand || 'unclassified'} - ${Math.round(noteEndTime - noteStartTime)}ms${note.isActive ? ' (holding)' : ''}`}
            >
              {/* Note content */}
              <div className="w-full h-full flex items-center justify-center">
                {width > 5 && noteHeight > 2 && (
                  <span className="text-white text-xs font-mono">
                    {getNoteNameFromMIDI(noteNumber)}
                  </span>
                )}
              </div>
            </div>
          );
        })}

        {/* Current time indicator - only visible during recording */}
        {isRecording && (
          <div className="absolute top-0 bottom-0 w-1 bg-red-500 z-20 shadow-lg" 
               style={{ right: '0%' }}>
            <div className="absolute top-0 w-full h-full bg-red-400 animate-pulse opacity-50"></div>
          </div>
        )}

      </div>
      
      {/* Horizontal Scrollbar - only show after recording if session is longer than window */}
      {needsScrollbar && (
        <div className="absolute bottom-0 left-16 right-0 h-8 bg-gray-800 border-t border-gray-600">
          <div className="flex items-center h-full px-2">
            <div className="text-xs text-gray-500 mr-2" title="Use arrow keys to navigate">
              ←→
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={scrollPosition}
              onChange={(e) => setScrollPosition(parseFloat(e.target.value))}
              className="flex-1 h-4 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
              style={{
                background: `linear-gradient(to right, #4F46E5 0%, #4F46E5 ${scrollPosition * 100}%, #374151 ${scrollPosition * 100}%, #374151 100%)`
              }}
            />
            <div className="ml-3 text-xs text-gray-400 whitespace-nowrap">
              {Math.floor((displayStartTime - sessionStartTime) / 1000)}s - {Math.floor((displayEndTime - sessionStartTime) / 1000)}s
            </div>
            <div className="ml-2 text-xs text-gray-500">
              / {Math.floor(totalSessionDuration / 1000)}s total
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomMidiRoll;
